/**
 * Where the API lives - decided once, used by every client in this folder.
 *
 * VITE_API_URL is substituted at build time, so a configured value pins the
 * bundle to one hostname. The app is served under more than one name, and a
 * bundle that calls a fixed host makes every other name cross-origin. That
 * breaks the session rather than just the request: the refresh token is an
 * HttpOnly cookie with SameSite=Lax, which the browser withholds on a
 * cross-site call, so the user is signed out at the first token refresh.
 *
 * Leaving it unset therefore means "the origin that served this page", which
 * makes every hostname self-contained. The dev server is the exception: it
 * serves the app on :3000 while the backend listens on :8000.
 */

const DEV_API_ORIGIN = 'http://localhost:8000';
const DEV_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

const isDevHost = (hostname: string): boolean => DEV_HOSTNAMES.has(hostname);

/**
 * Absolute base URL of the API, without a trailing slash and without `/api`.
 */
export const getApiBaseUrl = (): string => {
  const configured = import.meta.env.VITE_API_URL;

  if (configured) {
    // A page loaded over https must not call an http API: the browser blocks it
    // as mixed content, and the CSP connect-src rule rejects it before that.
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      return configured.replace(/^http:/, 'https:');
    }
    return configured;
  }

  // No window means SSR or a test runner - neither serves the app, so there is
  // no origin to inherit.
  if (typeof window === 'undefined') {
    return DEV_API_ORIGIN;
  }

  return isDevHost(window.location.hostname) ? DEV_API_ORIGIN : window.location.origin;
};

/**
 * WebSocket endpoint, derived from the same decision so the two can never
 * disagree about which host the backend is on.
 */
export const getWebSocketUrl = (): string => {
  const base = getApiBaseUrl();

  try {
    const url = new URL(base);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}/ws`;
  } catch (error) {
    // Only reachable through a malformed VITE_API_URL. The page's own origin is
    // the better guess at that point than refusing to connect at all.
    console.error('[WebSocket] Failed to parse API URL, using page origin', error);
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
  }
};
